export interface CaseData {
  id: string;
  type: string;
  typeLabel: string;
  icon: string;
  background: string;
  keyReason: string;
  results: string[];
  details: {
    profile: {
      industry: string;
      customerType: string;
      originalMode: string;
    };
    painPoints: Array<{
      title: string;
      description: string;
    }>;
    solutions: Array<{
      policy: string;
      implementation: string;
      systemModule?: string;
    }>;
    effects: string[];
    targetAudience: string;
  };
}

export const cases: CaseData[] = [
  {
    id: 'case-001',
    type: 'market',
    typeLabel: '批发市场商户',
    icon: 'ri-store-2-line',
    background: '广州白云区某服装批发市场商户，主营女装出口，客户遍布东南亚、中东地区，月均出口 30-50 票，单票金额 3-8 万美元',
    keyReason: '多品类混装 + 免征增值税 + 无需进项发票',
    results: ['流程稳定运行', '合规性提升', '结汇便捷'],
    details: {
      profile: {
        industry: '服装批发',
        customerType: '批发市场个体工商户',
        originalMode: '买单出口 / 无票出口'
      },
      painPoints: [
        {
          title: '进项发票问题',
          description: '上游供应商多为小作坊，无法提供增值税专用发票，导致无法正常退税'
        },
        {
          title: '多品类报关困难',
          description: '每票货物包含 50-200 个 SKU，传统报关模式申报复杂、时效慢'
        },
        {
          title: '合规风险',
          description: '长期使用买单出口，存在外汇、税务合规隐患'
        }
      ],
      solutions: [
        {
          policy: '免征增值税政策',
          implementation: '无需取得进项发票，直接以市场采购方式申报出口',
          systemModule: '免税申报模块'
        },
        {
          policy: '多品类集拼申报',
          implementation: '系统支持一票多品申报，自动生成报关单，大幅提升申报效率',
          systemModule: '智能报关模块'
        },
        {
          policy: '合规收汇结汇',
          implementation: '通过 1039 专用账户收汇，资金流向清晰，符合外汇管理要求',
          systemModule: '收汇结汇模块'
        }
      ],
      effects: [
        '出口流程完全合规化，消除买单出口风险',
        '申报效率提升 60%，单票申报时间从 2 小时缩短至 30 分钟',
        '结汇周期缩短至 T+1，资金周转效率显著提升',
        '业务规模稳定增长，月均出口额增长 40%'
      ],
      targetAudience: '如果您也是批发市场商户，经营多品类、小批量出口业务，且长期面临进项发票缺失问题，可以参考该路径使用 1039 市场采购贸易模式。'
    }
  },
  {
    id: 'case-002',
    type: 'ecommerce',
    typeLabel: '跨境电商本土店',
    icon: 'ri-shopping-cart-line',
    background: '深圳某跨境电商公司，主营家居用品，通过独立站销售至欧美市场，国内发货，月均出口 20-30 票，单票金额 5-12 万美元',
    keyReason: '合规出口通道 + 便捷结汇 + 物流可追溯',
    results: ['合规性保障', '结汇效率提升', '物流可控'],
    details: {
      profile: {
        industry: '跨境电商',
        customerType: '独立站卖家',
        originalMode: '个人代收款 / 灰色清关'
      },
      painPoints: [
        {
          title: '收款合规问题',
          description: '长期使用个人账户或第三方代收款，存在外汇监管风险'
        },
        {
          title: '物流清关不透明',
          description: '货物通过非正规渠道出口，物流信息不可追溯，客户投诉率高'
        },
        {
          title: '业务扩展受限',
          description: '无法提供正规出口单证，难以对接优质物流商和海外仓'
        }
      ],
      solutions: [
        {
          policy: '1039 合规出口',
          implementation: '通过市场采购方式正规报关出口，获得完整出口单证',
          systemModule: '报关申报模块'
        },
        {
          policy: '阳光化收汇',
          implementation: '使用 1039 专用收汇账户，资金流向合规，支持大额收款',
          systemModule: '外汇管理模块'
        },
        {
          policy: '物流全程可追溯',
          implementation: '对接平台合作物流商，实现从国内仓到海外仓的全程追踪',
          systemModule: '物流监控模块'
        }
      ],
      effects: [
        '收款完全合规化，消除外汇监管风险',
        '物流时效提升 30%，客户满意度显著提高',
        '成功对接 3 家优质海外仓，业务覆盖范围扩大',
        '年出口额突破 500 万美元，实现规模化增长'
      ],
      targetAudience: '如果您是跨境电商独立站卖家，从国内发货至海外，希望实现合规出口和阳光化收汇，可以参考该路径使用 1039 模式。'
    }
  },
  {
    id: 'case-003',
    type: 'agent',
    typeLabel: '境外采购代理',
    icon: 'ri-global-line',
    background: '广州某外贸代理公司，为境外客户提供国内采购代理服务，涉及电子配件、五金工具等多品类，月均代理出口 40-60 票',
    keyReason: '责任边界清晰 + 单证合规 + 风险可控',
    results: ['责任明确', '单证齐全', '风险隔离'],
    details: {
      profile: {
        industry: '外贸代理',
        customerType: '境外采购代理公司',
        originalMode: '一般贸易代理 / 买单出口'
      },
      painPoints: [
        {
          title: '责任边界模糊',
          description: '传统代理模式下，货物、资金、单证责任不清晰，容易产生纠纷'
        },
        {
          title: '利润空间有限',
          description: '代理费率低，但承担的合规风险和操作成本高'
        },
        {
          title: '上游供应商管理困难',
          description: '供应商分散，发票管理复杂，难以满足一般贸易退税要求'
        }
      ],
      solutions: [
        {
          policy: '1039 代理出口模式',
          implementation: '以市场采购方式代理出口，责任边界清晰，单证完整',
          systemModule: '代理申报模块'
        },
        {
          policy: '免税政策',
          implementation: '无需处理复杂的进项发票和退税流程，降低操作成本',
          systemModule: '税务管理模块'
        },
        {
          title: '供应链数字化管理',
          implementation: '通过系统统一管理上游供应商信息，实现采购、报关、物流全流程可视化',
          systemModule: '供应链管理模块'
        }
      ],
      effects: [
        '责任边界明确，客户纠纷率下降 80%',
        '操作成本降低 40%，利润空间提升',
        '供应商管理效率提升，采购周期缩短 30%',
        '业务规模扩大，月均代理出口额增长 50%'
      ],
      targetAudience: '如果您是外贸代理公司，为境外客户提供国内采购代理服务，希望明确责任边界、降低合规风险，可以参考该路径使用 1039 模式。'
    }
  },
  {
    id: 'case-004',
    type: 'no-invoice',
    typeLabel: '无进项贸易公司',
    icon: 'ri-file-list-line',
    background: '东莞某外贸公司，主营日用百货出口，上游供应商多为小型工厂和个体户，长期无法取得增值税专用发票',
    keyReason: '免征增值税 + 不退税 + 无需进项发票',
    results: ['税务合规', '成本降低', '流程简化'],
    details: {
      profile: {
        industry: '日用百货出口',
        customerType: '外贸公司',
        originalMode: '一般贸易（无法退税）'
      },
      painPoints: [
        {
          title: '进项发票缺失',
          description: '上游供应商无法提供增值税专用发票，导致无法享受出口退税政策'
        },
        {
          title: '税务成本高',
          description: '按一般贸易方式出口，需缴纳增值税但无法退税，税负成本高'
        },
        {
          title: '竞争力不足',
          description: '由于税务成本高，产品价格缺乏竞争力，订单流失严重'
        }
      ],
      solutions: [
        {
          policy: '1039 免税政策',
          implementation: '以市场采购方式出口，免征增值税，无需取得进项发票',
          systemModule: '免税申报模块'
        },
        {
          policy: '简化申报流程',
          implementation: '系统自动生成报关单，无需复杂的退税申报流程',
          systemModule: '智能报关模块'
        },
        {
          policy: '成本优化',
          implementation: '税务成本降低后，产品价格更具竞争力，订单量显著增加',
          systemModule: '成本核算模块'
        }
      ],
      effects: [
        '税务成本降低 13%（免征增值税），产品价格竞争力提升',
        '申报流程简化，操作效率提升 50%',
        '订单量增长 60%，业务规模快速扩大',
        '客户满意度提升，复购率提高 40%'
      ],
      targetAudience: '如果您是外贸公司，上游供应商复杂且无法提供增值税专用发票，希望降低税务成本、提升产品竞争力，可以参考该路径使用 1039 模式。'
    }
  },
  {
    id: 'case-005',
    type: 'supply-chain',
    typeLabel: '外贸供应链',
    icon: 'ri-links-line',
    background: '佛山某大型外贸公司的上游供应商，主营陶瓷、建材出口配套服务，被动配合外贸公司完成出口，利润有限但责任重',
    keyReason: '风险隔离 + 合规出货 + 责任明确',
    results: ['风险可控', '流程规范', '利润保障'],
    details: {
      profile: {
        industry: '建材供应链',
        customerType: '外贸公司上游供应商',
        originalMode: '配合外贸公司一般贸易出口'
      },
      painPoints: [
        {
          title: '责任风险高',
          description: '作为供应商，需承担货物质量、交期等责任，但对出口流程缺乏控制'
        },
        {
          title: '利润空间有限',
          description: '外贸公司压价严重，供应商利润微薄，但承担的风险和成本高'
        },
        {
          title: '合规性不足',
          description: '部分外贸公司操作不规范，供应商被动卷入合规风险'
        }
      ],
      solutions: [
        {
          policy: '1039 独立出口',
          implementation: '供应商自主使用 1039 模式出口，掌握出口流程主动权',
          systemModule: '独立申报模块'
        },
        {
          policy: '风险隔离',
          implementation: '通过 1039 模式，明确供应商与外贸公司的责任边界，降低连带风险',
          systemModule: '风控管理模块'
        },
        {
          policy: '利润优化',
          implementation: '减少中间环节，供应商直接对接境外客户，利润空间提升',
          systemModule: '利润核算模块'
        }
      ],
      effects: [
        '风险隔离效果显著，合规性问题零发生',
        '利润空间提升 20%，业务可持续性增强',
        '出口流程主动权掌握在手，客户满意度提升',
        '成功转型为独立出口商，业务规模扩大'
      ],
      targetAudience: '如果您是大型外贸公司的上游供应商，被动配合出口但希望掌握主动权、降低风险、提升利润，可以参考该路径使用 1039 模式。'
    }
  },
  {
    id: 'case-006',
    type: 'sensitive',
    typeLabel: '涉敏国家业务',
    icon: 'ri-shield-check-line',
    background: '广州某外贸公司，主营电子产品出口至中东、非洲等地区，面临严格的合规要求和物流监管',
    keyReason: '真实贸易 + 全程可追溯 + 合规保障',
    results: ['合规性强', '可追溯性高', '风险可控'],
    details: {
      profile: {
        industry: '电子产品出口',
        customerType: '涉敏国家外贸公司',
        originalMode: '一般贸易 / 部分灰色清关'
      },
      painPoints: [
        {
          title: '合规要求严格',
          description: '涉敏国家出口需满足严格的海关、外汇、物流监管要求'
        },
        {
          title: '物流风险高',
          description: '部分物流商操作不规范，导致货物滞留、退运等问题'
        },
        {
          title: '资金安全隐患',
          description: '收汇渠道不透明，存在资金被冻结、无法结汇等风险'
        }
      ],
      solutions: [
        {
          policy: '1039 合规出口',
          implementation: '通过市场采购方式正规报关，确保贸易真实性和可追溯性',
          systemModule: '合规申报模块'
        },
        {
          policy: '物流全程监控',
          implementation: '对接平台合作物流商，实现从国内到目的国的全程追踪',
          systemModule: '物流监控模块'
        },
        {
          policy: '阳光化收汇',
          implementation: '使用 1039 专用收汇账户，资金流向清晰，符合外汇管理要求',
          systemModule: '外汇管理模块'
        }
      ],
      effects: [
        '合规性全面提升，海关、外汇检查零问题',
        '物流时效和稳定性显著改善，客户投诉率下降 90%',
        '收汇安全有保障，资金周转效率提升',
        '业务规模稳定增长，成功开拓 5 个新市场'
      ],
      targetAudience: '如果您从事涉敏国家或地区的外贸业务，面临严格的合规要求和高风险挑战，希望实现真实贸易、全程可追溯，可以参考该路径使用 1039 模式。'
    }
  }
];